// ABOUTME: Reusable category preview component showing up to 6 content items per category
// ABOUTME: Displays both articles and videos with filtering and "View All" navigation

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  CardMedia,
  Chip,
  Button,
  Paper,
  Divider,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';

import { Article, VideoLibrary, ChevronRight, Visibility } from '@mui/icons-material';
import { getKBArticles, getKBVideos } from '../../services/api';
import { KBCategoryDto, KBArticleListDto, KBVideoDto } from '../../types/knowledgeBase';

interface CategorySamplerProps {
  category: KBCategoryDto;
  contentTypeFilter?: 'all' | 'articles' | 'videos';
  maxItems?: number;
  onCategoryClick?: (category: KBCategoryDto) => void;
}

const CategorySampler: React.FC<CategorySamplerProps> = ({
  category,
  contentTypeFilter = 'all',
  maxItems = 6,
  onCategoryClick,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [articles, setArticles] = useState<KBArticleListDto[]>([]);
  const [videos, setVideos] = useState<KBVideoDto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadContent intentionally omitted; it is stable and adding it would cause extra fetches
  }, [category.id, contentTypeFilter]);

  const loadContent = async () => {
    try {
      setLoading(true);

      const articlesPromise: Promise<KBArticleListDto[]> =
        contentTypeFilter === 'all' || contentTypeFilter === 'articles'
          ? getKBArticles().then((allArticles) =>
              allArticles.filter(
                (a) => a.categoryId === category.id && a.publishStatus === 'Published'
              )
            )
          : Promise.resolve([]);

      const videosPromise: Promise<KBVideoDto[]> =
        contentTypeFilter === 'all' || contentTypeFilter === 'videos'
          ? getKBVideos().then((allVideos) =>
              allVideos.filter(
                (v) => v.publishStatus === 'Published' && v.isUserFacingVisible
              )
            )
          : Promise.resolve([]);

      const [categoryArticles, allVideos] = await Promise.all([articlesPromise, videosPromise]);

      setArticles(categoryArticles.slice(0, maxItems));
      // For videos, we don't have category association, so we just show popular ones
      setVideos(allVideos.slice(0, maxItems));
    } catch (err) {
      console.error('Failed to load category content:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleArticleClick = (articleId: number) => {
    navigate(`/knowledge-base/article/${articleId}`);
  };

  const handleVideoClick = (videoId: number) => {
    navigate(`/knowledge-base/video/${videoId}`);
  };

  const handleViewAllClick = () => {
    if (onCategoryClick) {
      onCategoryClick(category);
    } else {
      navigate(`/knowledge-base?category=${category.id}`);
    }
  };

  const totalItems = articles.length + videos.length;
  const hasContent = totalItems > 0;
  const hasSubcategories = category.childCategories.length > 0;

  // Don't show empty categories
  if (!loading && !hasContent && !hasSubcategories) {
    return null;
  }

  return (
    <Paper sx={{ mb: 4, p: 3 }}>
      {/* Category Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5" component="h2" gutterBottom>
            {category.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {category.articleCount} article{category.articleCount !== 1 ? 's' : ''} •{' '}
            Level {category.level}
          </Typography>
        </Box>
        {hasContent && (
          <Button
            endIcon={<ChevronRight />}
            onClick={handleViewAllClick}
            sx={{ flexShrink: 0 }}
          >
            View All
          </Button>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Subcategories */}
      {hasSubcategories && (
        <Box mb={3}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Subcategories
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {category.childCategories.map((subcat) => (
              <Chip
                key={subcat.id}
                label={`${subcat.name} (${subcat.articleCount})`}
                onClick={() => onCategoryClick?.(subcat)}
                clickable
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Content Grid */}
      {hasContent ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {/* Articles */}
          {articles.map((article) => (
            <Box key={`article-${article.id}`} sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.333% - 11px)' } }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardActionArea
                  onClick={() => handleArticleClick(article.id)}
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                  }}
                >
                  {article.thumbnailUrl && (
                    <CardMedia
                      component="img"
                      height="140"
                      image={article.thumbnailUrl}
                      alt={article.title}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Article fontSize="small" color="primary" />
                      <Typography variant="caption" color="primary">
                        Article
                      </Typography>
                    </Box>

                    <Typography variant="h6" component="div" gutterBottom>
                      {article.title}
                    </Typography>

                    {article.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {article.description}
                      </Typography>
                    )}

                    {/* Metadata */}
                    <Box display="flex" gap={1} mt={2} alignItems="center">
                      <Visibility fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {article.viewCount} views
                      </Typography>
                    </Box>

                    {/* Tags */}
                    {article.tagNames.length > 0 && (
                      <Box display="flex" gap={0.5} mt={1} flexWrap="wrap">
                        {article.tagNames.slice(0, 2).map((tagName, idx) => (
                          <Chip key={idx} label={tagName} size="small" />
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Box>
          ))}

          {/* Videos */}
          {videos.map((video) => (
            <Box sx={{ xs: 12, sm: 6, md: 4 }} key={`video-${video.id}`}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardActionArea
                  onClick={() => handleVideoClick(video.id)}
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                  }}
                >
                  {video.thumbnailUrl && (
                    <Box position="relative">
                      <CardMedia
                        component="img"
                        height="140"
                        image={video.thumbnailUrl}
                        alt={video.title}
                      />
                      <Box
                        position="absolute"
                        bottom={8}
                        right={8}
                        bgcolor={alpha(theme.palette.common.black, 0.7)}
                        color="white"
                        px={1}
                        py={0.5}
                        borderRadius={1}
                      >
                        <Typography variant="caption">
                          {video.durationSeconds
                            ? `${Math.floor(video.durationSeconds / 60)}:${(
                                video.durationSeconds % 60
                              )
                                .toString()
                                .padStart(2, '0')}`
                            : 'Video'}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <VideoLibrary fontSize="small" color="secondary" />
                      <Typography variant="caption" color="secondary">
                        Video
                      </Typography>
                    </Box>

                    <Typography variant="h6" component="div" gutterBottom>
                      {video.title}
                    </Typography>

                    {video.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {video.description}
                      </Typography>
                    )}

                    {/* Metadata */}
                    <Box display="flex" gap={2} mt={2} alignItems="center">
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Visibility fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                          {video.viewCount}
                        </Typography>
                      </Box>
                      <Chip label={video.audienceTag} size="small" color="primary" />
                    </Box>

                    {/* Tags */}
                    {video.tags.length > 0 && (
                      <Box display="flex" gap={0.5} mt={1} flexWrap="wrap">
                        {video.tags.slice(0, 2).map((tag) => (
                          <Chip key={tag.id} label={tag.name} size="small" />
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Box>
          ))}
        </Box>
      ) : (
        !loading && (
          <Box textAlign="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              No content available in this category yet.
            </Typography>
          </Box>
        )
      )}
    </Paper>
  );
};

export default CategorySampler;
